import { createRootRouteWithContext, Outlet, useRouter } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import type { AuthContextType } from '@/types/auth'
import { Toaster } from 'sonner'
import { ErrorBoundary } from '@/components/shared/error-boundary'
import { RoutePending } from '@/components/shared/route-pending'
import { ScrollToTop } from '@/components/shared/scroll-to-top'
import { AlertTriangle, RefreshCw, LogIn } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'

interface RouterContext {
  auth: AuthContextType
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  pendingComponent: RoutePending,
  errorComponent: RootErrorComponent,
})

function RootComponent() {
  return (
    <ErrorBoundary>
      <ScrollToTop />
      <Outlet />
      <Toaster position="top-right" richColors />
    </ErrorBoundary>
  )
}

function RootErrorComponent({ error }: { error: Error }) {
  const router = useRouter()

  const handleRetry = () => {
    void router.invalidate()
  }

  const handleGoToLogin = () => {
    window.location.href = '/login'
  }

  // Verifica se é erro de autenticação
  const isAuthError = error.message?.toLowerCase().includes('auth') ||
    error.message?.toLowerCase().includes('session') ||
    error.message?.toLowerCase().includes('token')

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <CardTitle>
              {isAuthError ? 'Sessão expirada' : 'Algo deu errado'}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {isAuthError
              ? 'Sua sessão expirou ou houve um problema de autenticação. Por favor, faça login novamente.'
              : 'Ocorreu um erro inesperado. Por favor, tente novamente.'}
          </p>
          {import.meta.env.DEV && error && (
            <pre className="mt-4 overflow-auto rounded bg-muted p-2 text-xs">
              {error.message}
            </pre>
          )}
        </CardContent>
        <CardFooter className="flex gap-2">
          {isAuthError ? (
            <Button onClick={handleGoToLogin} className="gap-2">
              <LogIn className="h-4 w-4" />
              Ir para login
            </Button>
          ) : (
            <>
              <Button onClick={handleRetry} variant="outline" className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Tentar novamente
              </Button>
              <Button onClick={handleGoToLogin} variant="ghost" className="gap-2">
                <LogIn className="h-4 w-4" />
                Ir para login
              </Button>
            </>
          )}
        </CardFooter>
      </Card>
    </div>
  )
}
